import * as ActionTypes from "../actions/types";
import { formatApiError } from "../constants/apiErrorMessages";
import { mapApplicationData } from "../constants/map/application";

const INITIAL_STATE = {
  applicationsById: {},
  applicationIds: [],
  loading: false,
  errorMessage: null,
  deleteApplicationLoading: false,
  deleteApplicationModalIsOpen: false,
  unsubscribeFromApiProductModalIsOpen: false,
  unsubscribeFromApiProductLoading: false,
  apiProductUuidToUnsubscribe: null,
  unsubscribedFromApiProductSuccessfully: false,
  applicationModalOpen: false,
  applicationModalActionLoading: false,
  psdValidationCertificate: {
    isValid: false,
    message: "",
    certRequired: false,
  },
};

function validatePsdCertificate(state, action) {
  console.log("validatePsdCertificate", action.payload);
  const { errorMsg, errors, certRequired } = action.payload.meta;
  state.psdValidationCertificate.isValid = errors ? false : true;
  state.psdValidationCertificate.message = errors ? errorMsg : "";
  state.psdValidationCertificate.certRequired = certRequired;
}

function validatePsdCertificateFailed(state) {
  state.psdValidationCertificate.message =
    "An error has occured please try again later!";
}

function validatePsdCertificateReset(state) {
  state.psdValidationCertificate.isValid = false;
  state.psdValidationCertificate.message = "";
}

function createApplication(state) {
  state.errorMessage = null;
  state.loading = true;
}

function clearMessagesAndLoaders(state) {
  state.errorMessage = null;
  state.loading = false;
}

function showError(state, action) {
  if (
    action.payload?.error?.code === "DP-422" &&
    action.payload.error.description &&
    action.payload.error.description !== "Client Error"
  ) {
    // Core validation errors say what to fix, e.g. which redirect URI is not valid.
    state.errorMessage = action.payload.error.description;
    state.loading = false;
  } else if (action.payload?.errors) {
    state.errorMessage = action.payload.errors[0].title;
    state.loading = false;
  } else if (Array.isArray(action.payload) && action.payload[0]) {
    const errorMessage = action.payload[0].response
      ? action.payload[0].response.text
      : action.payload[0].rawResponse;
    state.errorMessage = errorMessage;
    state.loading = false;
  } else if (action.payload?.message) {
    // Handle simple error objects with message property
    state.errorMessage = action.payload.message;
    state.loading = false;
  } else {
    // Fallback for unknown error format
    state.errorMessage = "An error occurred. Please try again.";
    state.loading = false;
  }
}

function fetchApplication(state) {
  // Opening (or reloading) an application starts a fresh view: an error left over from a
  // previous action, e.g. a failed reset-secret, must not follow the user to the next page.
  state.errorMessage = null;
  state.loading = true;
}

function fetchApplicationFailed(state) {
  state.loading = false;
}

function deleteApplication(state) {
  state.deleteApplicationLoading = true;
}

function deleteApplicationSuccess(state) {
  state.deleteApplicationLoading = false;
  state.applicationsById = {};
  state.deleteApplicationModalIsOpen = false;
}

function deleteApplicationFailed(state, action) {
  state.deleteApplicationLoading = false;

  // Handle error message from failed deletion
  if (action.payload?.error) {
    // If error is an object with description or message
    if (typeof action.payload.error === "object") {
      // Core exception: shared wording (the Identity Server's "object reference" text is not for users)
      state.errorMessage = action.payload.error.code
        ? formatApiError(action.payload.error)
        : action.payload.error.description ||
          action.payload.error.message ||
          JSON.stringify(action.payload.error);
    } else {
      state.errorMessage = action.payload.error;
    }
  } else if (action.payload?.body?.exception) {
    // Handle 200 response with exception
    if (typeof action.payload.body.exception === "object") {
      state.errorMessage = formatApiError(action.payload.body.exception);
    } else {
      state.errorMessage = action.payload.body.exception;
    }
  } else if (action.payload?.message) {
    // Simple error message
    state.errorMessage = action.payload.message;
  } else {
    // Fallback error message
    state.errorMessage = "Failed to delete application. Please try again.";
  }
}

function openDeleteApplicationModal(state) {
  state.deleteApplicationModalIsOpen = true;
  state.errorMessage = null; // Clear any previous error messages
}

function closeDeleteApplicationModal(state) {
  state.deleteApplicationModalIsOpen = false;
  state.errorMessage = null; // Clear error message when closing
}

function openUnsubscribeFromApiProductModal(state, action) {
  state.unsubscribeFromApiProductModalIsOpen = true;
  state.apiProductUuidToUnsubscribe = action.payload.uuid;
}

function closeUnsubscribeFromApiProductModal(state) {
  state.unsubscribeFromApiProductModalIsOpen = false;
  state.unsubscribeFromApiProductLoading = false;
  state.apiProductUuidToUnsubscribe = null;
  state.unsubscribedFromApiProductSuccessfully = false;
}

function unsubscribeFromApiProduct(state) {
  state.unsubscribeFromApiProductLoading = true;
  state.loading = true;
}

function unsubscribeFromApiProductSuccess(state) {
  state.unsubscribedFromApiProductSuccessfully = true;
  state.unsubscribeFromApiProductLoading = false;
}

function unsubscribeFromApiProductFailed(state) {
  state.errorMessage = "Something went wrong. Try again later";
  state.unsubscribeFromApiProductLoading = false;
  state.loading = false;
}

function resetClientSecret(state, action) {
  state.applicationToBeReset = action.payload.applicationId;
  state.loading = true;
  state.errorMessage = null; // Clear any previous error messages
}

function resetClientSecretSuccess(state, action) {
  const appId = state.applicationToBeReset;
  if (state.applicationsById[appId]) {
    state.applicationsById[appId].clientSecret = action.payload;
  }
  state.loading = false;
  state.errorMessage = null; // Clear any previous error messages
}

function resetClientSecretFailed(state, action) {
  state.loading = false;
  // Handle error message from failed reset
  if (action.payload?.error) {
    // If error is an object with description or message
    if (typeof action.payload.error === "object") {
      // Core exception: shared wording (the Identity Server's "object reference" text is not for users)
      state.errorMessage = action.payload.error.code
        ? formatApiError(action.payload.error)
        : action.payload.error.description ||
          action.payload.error.message ||
          JSON.stringify(action.payload.error);
    } else {
      state.errorMessage = action.payload.error;
    }
  } else if (action.payload?.message) {
    state.errorMessage = action.payload.message;
  } else {
    state.errorMessage = "Failed to reset client secret. Please try again.";
  }
}
function openApplicationModal(state) {
  state.applicationModalOpen = true;
}

function closeApplicationModal(state) {
  state.applicationModalOpen = false;
}

function fetchApplications(state) {
  state.loading = true;
}

function storeApplications(state, action) {
  let { payload } = action;

  // Only handle new API format
  if (payload && payload.payload && payload.payload.data) {
    state.loading = false;
    const mappedData = mapApplicationData(payload);
    // Merge the new applications with existing ones
    state.applicationsById = {
      ...state.applicationsById,
      ...mappedData,
    };
  } else {
    state.loading = false;
  }
}

function subscribeApplication(state) {
  state.loading = true;
}

function subscribeFromApiProductFailed(state) {
  state.loading = false;
}

function fetchApplicationsByOrganization(state) {
  state.loading = true;
  state.errorMessage = null;
}

function fetchApplicationsByOrganizationSuccess(state, action) {
  let { payload } = action?.payload;

  if (payload && payload.payload && payload.payload.data) {
    // New API format
    state.loading = false;
    const mappedData = mapApplicationData(payload);
    // Replace all applications with the fetched ones from organizations
    state.applicationsById = mappedData || {};
    state.applicationIds = Object.keys(mappedData || {});
  } else {
    // No data, just clear loading
    state.loading = false;
    state.applicationsById = {};
    state.applicationIds = [];
  }
}

function fetchApplicationsByOrganizationFailed(state, action) {
  state.loading = false;
  state.errorMessage = "Failed to fetch applications from organizations";
}

export const application = {
  initialState: INITIAL_STATE,
  handlers: {
    [ActionTypes.CREATE_APPLICATION]: createApplication,
    [ActionTypes.EDIT_APPLICATION]: createApplication,
    [ActionTypes.CREATE_APPLICATION_SUCCESS]: clearMessagesAndLoaders,
    [ActionTypes.EDIT_APPLICATION_SUCCESS]: clearMessagesAndLoaders,
    [ActionTypes.CLEAR_APPLICATION_FORM_STATE]: clearMessagesAndLoaders,
    [ActionTypes.CREATE_APPLICATION_FAILED]: showError,
    [ActionTypes.EDIT_APPLICATION_FAILED]: showError,
    [ActionTypes.FETCH_APPLICATION]: fetchApplication,
    [ActionTypes.FETCH_APPLICATION_FAILED]: fetchApplicationFailed,
    [ActionTypes.STORE_APPLICATION]: storeApplications,
    [ActionTypes.DELETE_APPLICATION]: deleteApplication,
    [ActionTypes.DELETE_APPLICATION_SUCCESS]: deleteApplicationSuccess,
    [ActionTypes.DELETE_APPLICATION_FAILED]: deleteApplicationFailed,
    [ActionTypes.OPEN_DELETE_APPLICATION_MODAL]: openDeleteApplicationModal,
    [ActionTypes.CLOSE_DELETE_APPLICATION_MODAL]: closeDeleteApplicationModal,
    [ActionTypes.OPEN_UNSUBSCRIBE_FROM_API_PRODUCT_MODAL]:
      openUnsubscribeFromApiProductModal,
    [ActionTypes.CLOSE_UNSUBSCRIBE_FROM_API_PRODUCT_MODAL]:
      closeUnsubscribeFromApiProductModal,
    [ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT]: unsubscribeFromApiProduct,
    [ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT_SUCCESS]:
      unsubscribeFromApiProductSuccess,
    [ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT_FAILED]:
      unsubscribeFromApiProductFailed,
    [ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION_FAILED]:
      subscribeFromApiProductFailed,
    [ActionTypes.RESET_APPLICATION_CLIENT_SECRET]: resetClientSecret,
    [ActionTypes.RESET_APPLICATION_CLIENT_SECRET_SUCCESS]:
      resetClientSecretSuccess,
    [ActionTypes.RESET_APPLICATION_CLIENT_SECRET_FAILED]:
      resetClientSecretFailed,
    [ActionTypes.OPEN_APPLICATION_MODAL]: openApplicationModal,
    [ActionTypes.CLOSE_APPLICATION_MODAL]: closeApplicationModal,
    [ActionTypes.FETCH_APPLICATIONS]: fetchApplications,
    [ActionTypes.STORE_APPLICATIONS]: storeApplications,
    [ActionTypes.FETCH_APPLICATIONS_BY_ORGANIZATION]:
      fetchApplicationsByOrganization,
    [ActionTypes.FETCH_APPLICATIONS_BY_ORGANIZATION_SUCCESS]:
      fetchApplicationsByOrganizationSuccess,
    [ActionTypes.FETCH_APPLICATIONS_BY_ORGANIZATION_FAILED]:
      fetchApplicationsByOrganizationFailed,
    [ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION]: subscribeApplication,
    [ActionTypes.VALIDATE_PSD_CERTIFICATE_SUCCESS]: validatePsdCertificate,
    [ActionTypes.VALIDATE_PSD_CERTIFICATE_FAILED]: validatePsdCertificateFailed,
    [ActionTypes.VALIDATE_PSD_CERTIFICATE_RESET]: validatePsdCertificateReset,
  },
};
